/**
 * `lib/sidePanels.ts` — a small signal to close every open right-edge panel
 * (#2824). Each `ui/SideDrawer` registers while it is open. The demo guide's
 * Autoplay calls `closeSidePanels()` before it opens the demo phone, so the
 * phone never lands on top of a panel.
 */
import { closeDemoPhone, isDemoPhoneOpen } from "./demoPhone";

const closers = new Set<() => void>();

/** A drawer registers its own close here while it is open. Returns the unregister function. */
export function registerSidePanel(close: () => void): () => void {
  closers.add(close);
  // A panel that opens while the demo phone is out folds the phone first, so the
  // phone never covers it. A no-op outside a demo (the phone is never open).
  if (isDemoPhoneOpen()) closeDemoPhone();
  return () => {
    closers.delete(close);
  };
}

/** Ask every open panel to close. Returns how many were open. */
export function closeSidePanels(): number {
  const open = Array.from(closers);
  for (const close of open) close();
  return open.length;
}

/** True while a panel is still on the page (open, or sliding out). */
export function sidePanelOnPage(): boolean {
  return document.querySelector(".motion-drawer") !== null;
}

/**
 * Close every open panel, then run `fn` once none is left on the page (at
 * most 2 seconds later). Runs at once when no panel is open.
 */
export function afterSidePanelsClose(fn: () => void): void {
  closeSidePanels();
  if (!sidePanelOnPage()) {
    fn();
    return;
  }
  const started = Date.now();
  const tick = () => {
    if (!sidePanelOnPage() || Date.now() - started > 2000) fn();
    else window.setTimeout(tick, 80);
  };
  window.setTimeout(tick, 80);
}
